import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';

import BusyOverlay from '../common/busyOverlay';
import Button from '../common/button';
import ReplenishFilter from '../../containers/replenish/replenishFilter';
import { mapItemCountsByBarcode } from '../../utils/helpers';
import RefillPlanFeedback from './refillPlanFeedback';
import icons from './../../icons.svg';
import RefillPlanConfirmation from './modals/refillPlanConfirmation';
import Planogram from './planogram';

import { history } from '../../store';

const SCALE2_PLANOGRAM_POLL_MS = 10000;

const Replenish = ({
  cabinetActions,
  settingActions,
  replenishPlanActions,
  setting,
  cabinet,
  replenishPlan,
  refillSchema,
  products,
  planogramConfig,
  planogramActions,
  user,
  t,
  updateRefillStatus,
}) => {
  const [openPlanConfirmation, setOpenPlanConfirmation] = useState(false);
  const [planFailed, setPlanFailed] = useState(false);

  // scale2: the cabinet re-uploads the planogram (product, current count, stocked count, confidence per
  // lane) after every refill door close and after every confirmed restock, so poll it while the refill is
  // running to keep the lane view current
  useEffect(() => {
    if (
      cabinet.scaleGeneration !== 'scale2' ||
      cabinet.replenishment !== 'on' ||
      !cabinet.deviceCode ||
      !cabinet.group ||
      !planogramActions
    ) {
      return undefined;
    }
    const poll = setInterval(() => {
      planogramActions.fetchPlanogram(cabinet.deviceCode, cabinet.group);
    }, SCALE2_PLANOGRAM_POLL_MS);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cabinet.scaleGeneration,
    cabinet.replenishment,
    cabinet.deviceCode,
    cabinet.group,
  ]);

  useEffect(() => {
    if (cabinet.currentItems && replenishPlan?.plan) {
      const itemCountMap = mapItemCountsByBarcode(cabinet.currentItems);
      const planCountMap = mapItemCountsByBarcode(replenishPlan.plan);

      const isPlanFailed = replenishPlan.plan.some(
        item => (itemCountMap[item.barcode] || 0) < planCountMap[item.barcode]
      );

      setPlanFailed(isPlanFailed);
    }
  }, [cabinet.currentItems, replenishPlan]);

  if (openPlanConfirmation) {
    return (
      <RefillPlanConfirmation
        t={t}
        user={user}
        cabinet={cabinet}
        replenishPlan={replenishPlan}
        replenishPlanActions={replenishPlanActions}
        planFailed={planFailed}
        setConfirmationOpen={status => setOpenPlanConfirmation(status)}
        updateRefillStatus={updateRefillStatus}
      />
    );
  }

  return (
    <section
      className={`replenishment ${
        cabinet.replenishment === 'on' ? 'replenishment--on' : ''
      }`}
    >
      {(() => {
        /** if there are any erros in the app, show it here */
        if (replenishPlan.error && replenishPlan.error === 'plan_status') {
          return (
            <div className="replenishment__popup">
              <div className="replenishment__popup-text">
                <svg>
                  <use xlinkHref={`${icons}#icon-warning`} />
                </svg>
                <span className="replenishment__popup-text-negative">
                  {t(replenishPlan.error)}
                </span>
              </div>
              <div
                className="replenishment__popup-cancel"
                onClick={() => {
                  replenishPlanActions.clearError();
                }}
              >
                <span />
                <span />
              </div>
            </div>
          );
        }

        /** if error of any type, show it */
        if (cabinet.error) {
          return (
            <div className="replenishment__popup">
              <div className="replenishment__popup-text">
                <svg>
                  <use xlinkHref={`${icons}#icon-warning`} />
                </svg>
                <span className="replenishment__popup-text-negative">
                  {t(cabinet.error || 'error:forbidden')}
                </span>
              </div>
              <div
                className="replenishment__popup-cancel"
                onClick={() => {
                  cabinetActions.clearError();
                }}
              >
                <span />
                <span />
              </div>
            </div>
          );
        }

        /** if delay overlay after signin button is pressed */
        if (cabinet.replenishmentDelay) {
          return <BusyOverlay />;
        }

        /**show the refill page after */
        return (
          <Fragment>
            <div className="replenishment__title ">
              <div className="replenishment__title-name">{cabinet.name}</div>
              <div className="replenishment__title-id">
                {cabinet.deviceCode}
              </div>
            </div>
            {/* scale2: lanes are confirmed (count) and assigned (product) directly from the refill view,
                so the planogram sits between the cabinet name and the door buttons; tapping a lane opens the
                slot modal */}
            {cabinet.scaleGeneration === 'scale2' && (
              <div className="replenishment__planogram">
                <div className="replenishment__planogram-hint">
                  {t('common:placeItemsThenConfirm')}
                </div>
                <Planogram
                  replenishPlan={replenishPlan}
                  refillSchema={refillSchema}
                  t={t}
                  products={products}
                  cabinet={cabinet}
                  cabinetActions={cabinetActions}
                  planogramConfig={planogramConfig}
                  cabinetSetting={setting}
                />
              </div>
            )}
            <div className="replenishment__buttons">
              <Button
                text={t('cabinet:door_unlock')}
                action={() => {
                  cabinetActions.startReplenishment(
                    cabinet.deviceCode,
                    cabinet.group
                  );
                  cabinetActions.handleFetchCabinetInventory(
                    cabinet.deviceCode
                  );
                }}
                type="primary"
              />
              <Button
                text={t('cabinet:door_lock')}
                action={() => {
                  cabinetActions.endReplenishment(cabinet.deviceCode);
                  cabinetActions.handleFetchCabinetInventory(
                    cabinet.deviceCode
                  );

                  if (replenishPlan.plan && replenishPlan.plan.length > 0) {
                    setOpenPlanConfirmation(true);
                  }
                }}
                type="primary"
              />
            </div>
            {cabinet.replenishment === 'off' &&
              cabinet.scaleGeneration !== 'scale2' &&
              (cabinet.deviceSubTypeName === 'scale' ||
                cabinet.deviceSubTypeName === 'hybrid') && (
                <div className="replenishment__calibration">
                  <div
                    className="calibration__start"
                    onClick={() => {
                      cabinetActions.startCalibration(
                        cabinet.deviceCode,
                        cabinet.deviceSubType,
                        'scale1'
                      );
                      history.push('/calibrate');
                    }}
                  >
                    <svg>
                      <use xlinkHref={`${icons}#icon-balance-scale`} />
                    </svg>
                    {t('common:start_calibration')}
                    <svg>
                      <use xlinkHref={`${icons}#icon-arrow-right`} />
                    </svg>
                  </div>
                </div>
              )}

            {cabinet.replenishment === 'on' && cabinet.deviceCode && (
              <>
                {!cabinet.currentItemsLoading &&
                  replenishPlan?.plan?.length && (
                    <RefillPlanFeedback
                      t={t}
                      severity={planFailed ? 'warning' : 'success'}
                      message={
                        planFailed
                          ? 'inventoryInfoWarning'
                          : 'inventoryInfoSuccess'
                      }
                      icon={
                        planFailed ? 'icon-warning' : 'icon-checkmark-outline'
                      }
                    />
                  )}
                <ReplenishFilter
                  setting={setting}
                  updateFilter={value => {
                    settingActions.updateCabinetSettings(
                      value,
                      cabinet.deviceCode
                    );
                  }}
                  cabinet={cabinet}
                  t={t}
                />
              </>
            )}
          </Fragment>
        );
      })()}
    </section>
  );
};

const mapStateToProps = ({ oidc }) => ({ user: oidc.user });

export default connect(mapStateToProps)(translate()(Replenish));
